// Types
import { Point } from '../../../common/maths/geometry/pointTypes';

// Utils
import { getDistanceSquared } from '../../../common/maths/geometry/point';

// Constants
export enum ZoomPointMode {
    CACHED,
    CACHED_LIMITED,
    CENTRED,
    EVENT_POS,
    EVENT_POS_LIMITED_ZOOMED_OUT,
    EVENT_POS_LIMITED_ZOOMED_IN,
}

/**
 * Determines the zoom method to use based on the previous and next states.
 */
export const getZoomPointMode = (
    isCurrentlyZooming: boolean,
    cachedTransformationPoint: Point,
    nextScale: number,
    previousScale: number,
    scaleDelta: number,
    nextEventPosition: Point,
    previousEventPosition: Point,
    nextScroll: Point,
    previousScroll: Point,
): ZoomPointMode => {
    const isZoomingOut = scaleDelta < 0;
    const isZoomingIn = !isZoomingOut;

    // Zooming beyond 1 - always use the event position
    if (nextScale > 1) {
        if (isZoomingIn) return ZoomPointMode.EVENT_POS;

        return isCurrentlyZooming ? ZoomPointMode.CACHED : ZoomPointMode.EVENT_POS_LIMITED_ZOOMED_IN;
    }

    // During a single zoom operation where scale < 1, use the cached transformation point
    if (isCurrentlyZooming && cachedTransformationPoint) {
        if (previousScale >= 1 && nextScale < 1 && isZoomingOut) return ZoomPointMode.CENTRED;

        // When zooming out from a zoomed in state we want to continue to use the cached point
        //  otherwise we want to ensure that we're not showing empty space, so we need to use the cached limited one
        return previousScale >= 1 ? ZoomPointMode.CACHED : ZoomPointMode.CACHED_LIMITED;
    }

    if (isZoomingOut) return ZoomPointMode.CENTRED;

    const scrollMovement = getDistanceSquared(nextScroll, previousScroll);
    const eventPositionMovement = getDistanceSquared(nextEventPosition, previousEventPosition);

    // If the scroll is different or we've moved the mouse a lot, zoom into the mouse position
    if (scrollMovement > 200 || eventPositionMovement > 1000) {
        return ZoomPointMode.EVENT_POS_LIMITED_ZOOMED_OUT;
    }

    // Otherwise just use the cached limited point if we have it, and if not fallback to the centred point
    return cachedTransformationPoint ? ZoomPointMode.CACHED_LIMITED : ZoomPointMode.CENTRED;
};
